<template>
    <div>
        <div class="features-zone features-panel commands">

            <div class="feature-item">
                <base-button type="secondary"
                    class="btn-icon-only rounded-circle mr-0"
                    @click="infoModal.show = true"
                >
                    <span class="btn-inner--icon"><i class="far fa-clipboard"></i></span>
                </base-button>
            </div>

            <div class="feature-item">
                <base-button type="secondary" class="btn-icon-only rounded-circle mr-0"
                    @click="filtersModal.show = true"
                >
                    <span class="btn-inner--icon"><i class="fas fa-tasks"></i></span>
                </base-button>
            </div>

            <div class="feature-item" v-if="active">
                <base-button type="secondary"
                        class="btn-icon-only rounded-circle mr-0"
                        @click="createIssue">
                    <span class="btn-inner--icon"><i class="fas fa-exclamation"></i></span>
                    <span class="badge badge-md badge-circle badge-floating badge-warning"
                            v-if="stats && stats.global.issues">
                        {{ stats.global.issues }}
                    </span>
                </base-button>
            </div>

            <div class="feature-item" v-if="active">
                <base-button type="secondary"
                        class="btn-icon-only rounded-circle mr-0"
                        @click="createOrUpdateNote">
                    <span class="btn-inner--icon"><i class="fas fa-edit"></i></span>
                    <span class="badge badge-md badge-circle badge-floating badge-warning"
                            v-if="stats && stats.global.notes">
                        {{ stats.global.notes }}
                    </span>
                </base-button>
            </div>
        </div>

        <div class="features-zone timeslots">
            <div class=" features-panel">
                <div class="features-item" v-for="(timeslot, index) in sortedTimeslots" :key="index" :class="timeslotZoneClass(timeslot)">
                    <base-button type="white" :class="timeslotButtonClass(timeslot)" @click="timeslotClick(timeslot)">
                        {{ timeslotLabel(timeslot) }}
                    </base-button>
                </div>
            </div>
        </div>

        <portal to="modals">

            <form-modal
                v-model="filtersModal"
                size="lg"
                backdrop="true"
                :title="$t('common.filters')"
                :centered="false"
            >
                <filters :value="filters" @input="filtersChanged"
                    :types="types"
                    :competencies="competencies"
                    :lang="lang"
                    :restore="true"
                    :modal="filtersModal"
                >
                </filters>
            </form-modal>

            <form-modal
                v-model="infoModal"
                size="xl"
                backdrop="true"
                :title="degree.name + ' / ' + simulation.name"
                :centered="false"
            >
                <log-info :log="log"></log-info>
                <div class="text-center">
                    <base-button type="primary" class="ml-auto" @click="infoModal.show = false">
                        {{ $t('common.close') }}
                    </base-button>
                </div>
            </form-modal>

            <form-modal v-model="modals.editIssue"
                size="xl"
                :title="$t('common.issues')"
                :loading="issueForm.loading"
                :scroll-reset="false"
                :animationDuration="0"
                :centered="false"
            >
                <new-existing-tabs>
                    <template v-slot:new>
                        <issue-form v-model="issueForm.data"
                            :errors="issueForm.errors"
                            :modal="modals.editIssue"
                        >
                        </issue-form>
                    </template>
                    <template v-slot:existing>
                        <issues ref="issues"
                            :modal="modals.editIssue"
                            :registration="registration"
                            :simulation="simulation"
                            :session="session"
                            @update="updateIssue"
                            @remove="removeIssue"
                        >
                        </issues>
                    </template>
                </new-existing-tabs>
            </form-modal>

            <form-modal v-model="modals.editNote"
                :title="$t('dashboard.general-comment')"
                :loading="noteForm.loading"
                size="xl"
                :centered="false"
            >
                <note-form v-model="noteForm.data"
                    :errors="noteForm.errors"
                    :modal="modals.editNote"
                    :features="features"
                >
                </note-form>
            </form-modal>

            <form-modal v-model="modals.updateIssue"
                :title="$t('common.issue')"
                :loading="issueFormForUpdate.loading"
                size="xl"
                :centered="false"
            >
                <issue-form v-model="issueFormForUpdate.data"
                    :errors="issueFormForUpdate.errors"
                    :modal="modals.updateIssue"
                >
                </issue-form>
            </form-modal>

            <confirm-modal v-model="modals.confirmDeleteIssue">
                <p>{{ $t('issues.delete-confirm') }}</p>
            </confirm-modal>

        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import CrudForm from '@trax/classes/CrudForm'
    import CrudClient from '@trax/classes/CrudClient'
    import NewExistingTabs from './NewExistingTabs'
    import NoteForm from './NoteForm'
    import IssueForm from './IssueForm'
    import Issues from './Issues'
    import Filters from './Filters'
    import LogInfo from './LogInfo'
    import moment from 'moment'

    export default {

        components: {
            NewExistingTabs, NoteForm, IssueForm, Issues, Filters, LogInfo, // PictureForm, PictureFileForm, Feedbacks
        },

        props: {

            log: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
                required: true
            },

            degree: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            progress: {
                type: Object,
                default: {}
            },

            visibility: {
                type: Object,
                default: {}
            },

            session: {
                type: Object,
                default: null
            },

            stats: {
                type: Object,
                default: null
            },

            competencies: {
                type: Object,
                default: null
            },

            lang: {
                type: String,
                default: "en"
            },
            
            types: {
                type: Object,
                default: {}
            },

            filters: {
                type: Object,
                default: {}
            },

            features: {
                default: null
            },

            role: {
                type: String,
                default: 'instructor'
            },
        },

        data() {
            const context = this.session ? 'session' : 'log'
            const feedbackEndpoint = '/socrates/api/front/dashboard/data/context/' + context + '/feedbacks'
            const issueEndpoint = '/socrates/api/front/dashboard/data/context/' + context + '/issues'

            let feedbackClient = new CrudClient(feedbackEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            let issueClient = new CrudClient(issueEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            return {
                feedbackEndpoint,
                issueEndpoint,
                feedbackClient,
                issueClient,
                noteForm: new CrudForm(feedbackClient, this.mapNote, this.unmapNote),
                issueForm: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                issueFormForUpdate: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                modals: new ModalsController([
                    'editNote',
                    'editIssue', 'updateIssue', 'confirmDeleteIssue'
                ]),
                filtersModal: { show: false },
                infoModal: { show: false },
                moment: moment,
            }
        },

        created() {
            this.noteForm.init(this, 'noteForm')
            this.issueForm.init(this, 'issueForm')
            this.issueFormForUpdate.init(this, 'issueFormForUpdate')
            this.modals.init(this, 'modals')
        },

        computed: {

            active() {
                return true
            },

            sortedTimeslots() {
                return Object.values(this.progress).sort((a, b) => a.time >= b.time ? 1 : -1)
            }
        },

        methods: {
            
            timeslotClick(timeslot) {
                this.$emit('timeslotClicked', timeslot.id)
            },

            timeslotLabel(timeslot) {
                return timeslot.is_instruction && !timeslot.show_time ? 'INST' : timeslot.time
            },

            timeslotButtonClass(timeslot) {
                if (timeslot.is_instruction) {
                    return 'text-muted'
                }
                return {
                    'not-attempted': '',
                    'incomplete': 'text-warning',
                    'completed': 'text-success',
                }[timeslot.progress]
            },

            timeslotZoneClass(timeslot) {
                return this.visibility[timeslot.id] ? 'timeslot-visible' : ''
            },

            refresh() {
                this.$emit('refresh')
            },

            filtersChanged($event) {
                this.filtersModal.show = false
                this.$emit('filtersChanged', $event)
            },

            createOrUpdateNote() {
                this.modals.open('editNote')
                this.noteForm.update(0).then(resp => {
                    this.modals.close('editNote')
                    this.refresh()
                })
            },

            createIssue() {
                this.modals.open('editIssue')
                this.issueForm.create().then(resp => {
                    this.modals.close('editIssue')
                    this.refresh()
                })
            },

            updateIssue(issue) {
                this.modals.open('updateIssue')
                this.issueFormForUpdate.update(issue).then(resp => {
                    this.modals.close('updateIssue')
                    this.resetModal('editIssue')
                    this.$refs.issues.list()
                    this.refresh()
                })
            },

            removeIssue(issue) {
                this.modals.open('confirmDeleteIssue').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editIssue')
                        this.issueClient.delete(issue.id).then(resp => {
                            this.$refs.issues.list()
                            this.refresh()
                        })
                    }
                })
            },

            resetModal(name) {
                this.modals.close(name)
                this.$nextTick(() => {
                    this.modals.open(name)
                });
            },

            mapNote(feedback) {
                return this.mapFeedback(feedback)
            },

            mapFeedback(feedback) {
                return {
                    id: feedback ? feedback.id : null,
                    remote_id: feedback ? feedback.remote_id : null,
                    description: feedback ? feedback.description : null,
                }
            },

            mapIssue(issue) {
                return {
                    id: issue ? issue.id : null,
                    remote_id: issue ? issue.remote_id : null,
                    log_time_from: issue ? issue.log_time_from : null,
                    log_time_to: issue ? issue.log_time_to : null,
                    entered_at: issue ? issue.entered_at : null,
                    summary: issue ? issue.summary : null,
                    description: issue ? issue.description : null,
                }
            },

            unmapNote(data) {
                return this.unmapFeedback(data, 'note')
            },

            unmapFeedback(data, type) {
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.type = type
                data.timeslot_id = null
                return data
            },

            unmapIssue(data) {
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.entered_at = this.moment().format('YYYY-MM-DD HH:mm:ss')
                data.timeslot_id = null
                data.author_role = this.role
                return data
            },
        }
    }
</script>

<style scoped>

    .features-zone {
        position: fixed;
        z-index: 900;
        width: 70px;
    }
    .features-panel {
        background-color: #fff;
        box-shadow: 0 0 2rem 0 rgba(136, 152, 170, .15);
        border-radius: 0.5rem;
        text-align: center;
    }

    .features-panel >>> button .badge {
        border: none;
        width: 1.4rem;
        height: 1.4rem;
    }

    .commands {
        top: 78px;
        padding: 5px 0 5px 0;
    }
    .commands button {
        background-color: #fff;
        box-shadow: none;
        border-color: #fff;
    }
    .commands button:active, .commands button:active:focus {
        border: none;
        box-shadow: none !important;
    }

    .timeslots {
        top: 260px;
        bottom: 100px;
        overflow-y: auto;
    }
    .timeslots .features-panel {
        padding: 8px 0 8px 0;

    }
    .timeslots button {
        border-color: #fff;
        box-shadow: none;
        padding: 4px;
    }
    .timeslots button:hover {
        box-shadow: none;
        transform: none;
    }
    .timeslots button.text-muted {
        color: #b4c8dd !important;
    }
    .timeslots .timeslot-visible {
        border-left: solid 3px #b4c8dd;
    }

</style>
